/**
 * `useSettingsCommandsFromAgent` — settings & org `lx_*` tools from the
 * concierge.
 *
 * Concierge slash-commands Part A §2.6 (handoff
 * `concierge-slash-commands-lexide-tools-handoff-2026-06-30.md`).
 * Subscribes to `session.tool` broadcasts and dispatches the 4
 * settings/org tools to the right store/router.
 *
 * Wires:
 *   • `lx_open_settings {tab?}` → router `navigate("/settings" + hash)`.
 *   • `lx_open_schedule {jobId?}` → the Agendamentos pane (or
 *     `/schedule?job=` when deep-linking one job).
 *     The SettingsLayout owns the tab dispatch internally; this hook
 *     just delivers the hash. Unknown tabs are tolerated by the layout
 *     (it falls back to its default tab).
 *   • `lx_open_org_page {page}` → router `navigate("/org/" + page)`.
 *     The OrgAdminLayout owns the `/org/*` wildcard. Unknown sub-pages
 *     fall through to the layout's empty-state.
 *   • `lx_set_theme {theme}` → `useThemeStore.setPref(theme)`. The
 *     handoff §2.6 / §14 named `setTheme`; the real method is
 *     `setPref` (it writes to localStorage + applies the effective
 *     class on `<html>` in one step). Validates against the
 *     `ThemePreference` union (`"light" | "dark" | "system"`).
 *   • `lx_set_lang {code}` → `useLanguageStore.setLang(code)`.
 *     Validates against `LanguagePreference` (`"pt-BR" | "en-US" |
 *     "es-ES"`).
 *
 * Mount once at the App level (works from any route) — see App.tsx's
 * AppShell mount block.
 *
 * Contract (frozen by the runtime — DO NOT reshape): act on
 * `phase === "start"` only; accept `data.toolName ?? data.name` and
 * `data.toolInput ?? data.args`. Pure `handleSettingsCommand` extracted
 * for unit tests; the React wrapper just builds the deps + subscribes.
 */

import { useNavigate } from "react-router-dom";
import { useEventStream } from "./useEventStream";
import { useThemeStore } from "@/stores/useThemeStore";
import { useLanguageStore } from "@/stores/useLanguageStore";
import { useUIStore } from "@/stores/useUIStore";
import type { ThemePreference } from "@/lib/theme";
import type { LanguagePreference } from "@/lib/language";

export interface AgentEventPayloadRuntime {
  data?: {
    phase?: string;
    toolName?: string;
    name?: string;
    toolInput?: Record<string, unknown>;
    args?: Record<string, unknown>;
    [key: string]: unknown;
  };
}

const THEMES: readonly ThemePreference[] = ["light", "dark", "system"];
const LANGS: readonly LanguagePreference[] = ["pt-BR", "en-US", "es-ES"];

function isTheme(v: unknown): v is ThemePreference {
  return typeof v === "string" && (THEMES as readonly string[]).includes(v);
}
function isLang(v: unknown): v is LanguagePreference {
  return typeof v === "string" && (LANGS as readonly string[]).includes(v);
}

/** Strip path-traversal segments from a sub-page value before composing
 *  the org route. The router would reject `/org/../config` anyway, but
 *  trimming defensively keeps the navigation tidy. */
function sanitizeOrgPage(page: string): string | null {
  if (!page) return null;
  const trimmed = page.trim().replace(/^\/+|\/+$/g, "");
  if (!trimmed) return null;
  if (trimmed.includes("..") || trimmed.includes("\\")) return null;
  return trimmed;
}

export interface SettingsCommandDeps {
  navigate: (path: string) => void;
  /** Open the Agendamentos focus pane — the live destination for the retired
   *  cron settings tab (§4.5 alias). */
  openSchedulePane: () => void;
  setTheme: (theme: ThemePreference) => void;
  setLang: (lang: LanguagePreference) => void;
}

export function handleSettingsCommand(
  payload: unknown,
  deps: SettingsCommandDeps,
): void {
  const typed = payload as AgentEventPayloadRuntime;
  if (typed.data?.phase !== "start") return;
  const tool = typed.data?.toolName ?? typed.data?.name;
  const input = typed.data?.toolInput ?? typed.data?.args;

  switch (tool) {
    case "lx_open_settings": {
      const tab = input?.tab;
      // §4.5 alias, HASH form. `lx_open_settings {tab:"cron"}` predates the
      // Agendamentos rail pane and used to land on the settings CronTab —
      // which S-R2 P4 retired, so `#cron` is now a DEAD destination: a lawyer
      // asking for their schedules would get a settings page. Route it to the
      // pane, exactly as the `?tab=cron` query form already does. Kept as an
      // accepted INBOUND alias forever; the concierge is expected to prefer
      // `ide.schedule.open` (recurring-jobs' op), but this tool still wins
      // tool-selection today, so the alias is what makes that harmless.
      if (tab === "cron") {
        deps.openSchedulePane();
        return;
      }
      if (typeof tab === "string" && tab.length > 0) {
        // Sanitize: only allow word chars + hyphen in the hash fragment.
        const safe = tab.replace(/[^A-Za-z0-9_-]/g, "");
        deps.navigate(safe ? `/settings#${safe}` : "/settings");
      } else {
        deps.navigate("/settings");
      }
      return;
    }
    // recurring-jobs 34ee059b5 — the concierge's navigate-to-Agendamentos tool.
    // Deliberately a thin lx_ TOOL over the UNCHANGED `ide.schedule.open` OP:
    // dots are illegal in OpenAI function names, so exposing the op directly
    // took down every chat turn on galvao 2026-07-27 (one invalid entry rejects
    // the WHOLE tools array). The op keeps its P0-frozen contract and its
    // platform-ops handler; this is the model-facing door, and it rides the
    // baked `lexiun` alias so it needs no security-floor change.
    //
    // `jobId` matches the op's param name + optionality exactly, so the two
    // entry points into the same pane cannot drift.
    case "lx_open_schedule": {
      const jobId = input?.jobId;
      if (typeof jobId === "string" && jobId.trim().length > 0) {
        deps.navigate(`/schedule?job=${encodeURIComponent(jobId.trim())}`);
      } else {
        deps.openSchedulePane();
      }
      return;
    }
    case "lx_open_org_page": {
      const page = input?.page;
      if (typeof page !== "string") return;
      const safe = sanitizeOrgPage(page);
      if (!safe) return;
      deps.navigate(`/org/${safe}`);
      return;
    }
    case "lx_set_theme": {
      const theme = input?.theme;
      if (!isTheme(theme)) return;
      deps.setTheme(theme);
      return;
    }
    case "lx_set_lang": {
      const code = input?.code;
      if (!isLang(code)) return;
      deps.setLang(code);
      return;
    }
    default:
      return;
  }
}

export function useSettingsCommandsFromAgent(): void {
  const navigate = useNavigate();
  const setTheme = useThemeStore((s) => s.setPref);
  const setLang = useLanguageStore((s) => s.setLang);
  const openFocusPane = useUIStore((s) => s.openFocusPane);

  useEventStream("session.tool", (payload) => {
    handleSettingsCommand(payload, {
      navigate,
      setTheme,
      setLang,
      openSchedulePane: () => openFocusPane("schedule"),
    });
  });
}
